import {useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';

import ProductModel from 'coreClasses/product/ProductModel';
import VariantModel from 'coreClasses/product/VariantModel';
import {RootState} from 'coreStore/types';
import {productsEqualityCheck, selectProductsByIds} from 'coreStore/data/products';
import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

/**
 * Hook for selecting products
 *
 * @param {string[]} ids - product ids
 * @param {boolean} lazy - lazy load
 * @param {boolean} skipVariantFetch - when true, skip fetching individual variants for VGs (marks them ready immediately)
 * @returns {Object[]} products
 */
const useProducts = (ids: string[] = [], lazy: boolean = false, skipVariantFetch: boolean = false): ProductModel[] | VariantModel[] => {
	const {managerRepository} = useGeneralProvider();
	const storedProducts = useSelector((state: RootState) => selectProductsByIds(state, ids), productsEqualityCheck);
	const productsToRefresh = useMemo(() => storedProducts?.filter(p => p?.toBeRefreshed), [storedProducts] );

	useEffect(() => {
		if (!lazy && ids?.length) {
			managerRepository.getProductsMgr().getProducts(
				ids,
				false,
				{skipVariantFetch}
			);
		}
	}, [ids?.join(','), lazy, skipVariantFetch]);

	useEffect(() => {
		if (productsToRefresh?.length > 0) {
			managerRepository.getProductsMgr().getProducts(
				productsToRefresh.map((p) => p.id),
				false
			);
		}
	}, [productsToRefresh]);

	return useMemo(() => {
		return (storedProducts || []).map((p) => managerRepository.getProductsMgr().create(p));
	}, [storedProducts]);
};

export default useProducts;
